import styled from "@emotion/styled/macro";
import {
  Button,
  Skeleton,
  SvgIcon,
  SvgNames,
  TABLET_BREAKPOINT,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import { useCapContext } from "../../providers/CapContext";
import SunrunLogo from "components/assets/images/sunrun-logo.svg";

type CapSimplifiedPageHeaderProps = {
  isLoading: boolean;
  isMobile: boolean;
  showSharePresentation: boolean;
  showReadSignDocs: boolean;
  onSharePresentation: () => void;
  onReadSignDocs: () => void;
};

const joinPresent = (parts: Array<string | undefined>, separator: string) =>
  parts.filter(Boolean).join(separator);

const CapSimplifiedPageHeader = ({
  isLoading,
  isMobile,
  showSharePresentation,
  showReadSignDocs,
  onSharePresentation,
  onReadSignDocs,
}: CapSimplifiedPageHeaderProps) => {
  const { activeProposal, contacts, prospect } = useCapContext();
  const primaryContact = contacts?.find((c) => c.primary);

  const customerName = joinPresent(
    [primaryContact?.customerFirstName, primaryContact?.customerLastName],
    " "
  );
  const address = prospect
    ? joinPresent(
        [
          prospect.customerStreet,
          joinPresent(
            [
              prospect.customerCity,
              prospect.customerState,
              prospect.customerZipCode,
            ],
            ", "
          ),
        ],
        ", "
      )
    : "";
  const utility = activeProposal?.utility;
  const utilityLabel =
    utility && prospect?.customerState
      ? `${utility} (${prospect.customerState})`
      : utility ?? "";

  if (isLoading) {
    return (
      <HeaderRoot data-testid="cap-simplified-page-header">
        <HeaderInner isMobile={isMobile}>
          <TitleSkeleton />
          <BarSkeleton />
        </HeaderInner>
      </HeaderRoot>
    );
  }

  return (
    <HeaderRoot data-testid="cap-simplified-page-header">
      <HeaderInner isMobile={isMobile}>
        <TopRow isMobile={isMobile}>
          <PageTitle color={tokens.BRAND_HERO_BLUE} align="left">
            Final design approval
          </PageTitle>
          <ActionsContainer>
            <TopActions>
              {showSharePresentation ? (
                <IconActionButton
                  type="button"
                  aria-label="Share presentation"
                  onClick={onSharePresentation}
                >
                  <SvgIcon
                    name={SvgNames.Share}
                    width={24}
                    height={24}
                    color={tokens.BRAND_HERO_BLUE}
                  />
                </IconActionButton>
              ) : null}
              {showReadSignDocs ? (
                <ReadSignButton
                  type="button"
                  iconRight={
                    <SvgIcon
                      name={SvgNames.PenToolSvg}
                      width={24}
                      height={24}
                      color={tokens.WHITE}
                    />
                  }
                  onClick={onReadSignDocs}
                >
                  <Typography
                    size={16}
                    fontWeight="medium"
                    color={tokens.WHITE}
                  >
                    Read & sign docs
                  </Typography>
                </ReadSignButton>
              ) : null}
            </TopActions>
            <LogoImage src={SunrunLogo} alt="Sunrun" />
          </ActionsContainer>
        </TopRow>
        <FrostedBar isMobile={isMobile}>
          <FrostedBarContent>
            {customerName ? (
              <BarCustomerName size={16} color={tokens.BRAND_HERO_BLUE}>
                {customerName}
              </BarCustomerName>
            ) : null}
            {address ? (
              <BarText size={14} color={tokens.BRAND_HERO_BLUE}>
                {address}
              </BarText>
            ) : null}
            {utilityLabel ? (
              <BarText size={14} color={tokens.BRAND_HERO_BLUE}>
                {utilityLabel}
              </BarText>
            ) : null}
          </FrostedBarContent>
        </FrostedBar>
      </HeaderInner>
    </HeaderRoot>
  );
};

const HeaderRoot = styled.header`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 64px 8px;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    padding: 16px 0 0;
  }
`;

const HeaderInner = styled.div<{ isMobile: boolean }>`
  box-sizing: border-box;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? "16px" : "24px")};

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    padding: 0 16px;
  }
`;

const TopRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    flex-direction: column-reverse;
  }
`;

const PageTitle = styled(Typography)`
  font-size: 40px !important;
  font-weight: 400 !important;
  line-height: 110% !important;
  letter-spacing: -0.02em;
  justify-self: start;
`;

const TopActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-self: end;
  justify-content: space-between;

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    justify-self: start;
    flex-wrap: wrap;
    flex-direction: row-reverse;
    width: 100%;
  }
`;

const IconActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  border: 1px solid ${tokens.BRAND_HERO_BLUE};
  background: transparent;
  cursor: pointer;
`;

const ReadSignButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 8px 16px;
  border-radius: 999px;
  background: ${tokens.BRAND_HERO_BLUE};
  border: none;
`;

const LogoImage = styled.img`
  width: 130px;
  height: auto;
  justify-self: end;
  filter: brightness(0) saturate(100%) invert(11%) sepia(28%) saturate(1450%)
    hue-rotate(198deg) brightness(95%) contrast(95%);

  @media (max-width: ${TABLET_BREAKPOINT}px) {
    width: 106px;
    justify-self: start;
  }
`;

const FrostedBar = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  min-height: 84px;
  padding: ${({ isMobile }) => (isMobile ? "16px" : "20px 24px")};
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-sizing: border-box;
`;

const FrostedBarContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 24px;
  width: 100%;
`;

const BarCustomerName = styled(Typography)`
  line-height: 140% !important;
  font-weight: 700 !important;
`;

const BarText = styled(Typography)`
  line-height: 140% !important;
  font-weight: 400 !important;
`;

const TitleSkeleton = styled(Skeleton)`
  width: 320px;
  height: 44px;
`;

const BarSkeleton = styled(Skeleton)`
  width: 100%;
  height: 84px;
  border-radius: 10px;
`;

export { CapSimplifiedPageHeader };
